import React, { useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

interface Category {
  id: number;
  name: string;
}

interface CategorySelectorProps {
  availableCategories: Category[];
  selectedCategoryIds: number[];
  onSelectionChange: (categoryIds: number[]) => void;
  isLoading?: boolean;
  maxHeight?: string;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  availableCategories,
  selectedCategoryIds,
  onSelectionChange,
  isLoading = false,
  maxHeight = 'max-h-96',
}) => {
  const handleCategoryToggle = (categoryId: number) => {
    const newSelection = selectedCategoryIds.includes(categoryId)
      ? selectedCategoryIds.filter((id) => id !== categoryId)
      : [...selectedCategoryIds, categoryId];
    onSelectionChange(newSelection);
  };

  const selectedCategories = useMemo(
    () => availableCategories.filter((cat) => selectedCategoryIds.includes(cat.id)),
    [availableCategories, selectedCategoryIds]
  );

  const unselectedCategories = useMemo(
    () => availableCategories.filter((cat) => !selectedCategoryIds.includes(cat.id)),
    [availableCategories, selectedCategoryIds]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (availableCategories.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center">
        <p className="text-sm text-gray-500">No categories available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selected Categories */}
      {selectedCategories.length > 0 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <p className="mb-2 text-sm font-semibold text-green-900">Selected Categories</p>
          <div className="space-y-2">
            {selectedCategories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-2">
                <Checkbox
                  id={`selected-cat-${cat.id}`}
                  checked={true}
                  onCheckedChange={() => handleCategoryToggle(cat.id)}
                />
                <Label
                  htmlFor={`selected-cat-${cat.id}`}
                  className="flex-1 cursor-pointer font-medium text-green-900"
                >
                  {cat.name}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Categories */}
      {unselectedCategories.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold text-gray-700">Available Categories</p>
          <ScrollArea className={`rounded-lg border p-2 ${maxHeight}`}>
            <div className="space-y-2 pr-4">
              {unselectedCategories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-2 rounded-lg p-2 hover:bg-gray-50">
                  <Checkbox
                    id={`cat-${cat.id}`}
                    checked={false}
                    onCheckedChange={() => handleCategoryToggle(cat.id)}
                  />
                  <Label htmlFor={`cat-${cat.id}`} className="flex-1 cursor-pointer text-sm">
                    {cat.name}
                  </Label>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Summary */}
      {selectedCategories.length > 0 && (
        <div className="border-t pt-3">
          <Badge variant="outline" className="bg-green-100 text-green-800">
            {selectedCategories.length} categor{selectedCategories.length !== 1 ? 'ies' : 'y'} selected
          </Badge>
        </div>
      )}
    </div>
  );
};

export default CategorySelector;
